import { ImagePaths } from '../../lib/imageUrls.js';

export const aiMasterPromptEngineering = {

    id: 13,
    slug: 'ai-master-prompt-engineering',
    ogImage: '/og/courses/ai-master-prompt-engineering.jpg',
    courseCode: 'AI-PROMPT-001',
    title: 'AI Architect: The Master Course in Prompt Engineering',
    subtitle: 'Definitive masterclass in advanced prompt design and AI application development',
    valueProposition: 'Master advanced prompt engineering from foundational principles to multi-modal design, API-level instruction, prompt chaining, and building production-grade prompt-based applications.',
    duration: '120 hours',
    students: 'New',
    level: 'Advanced',
    image: ImagePaths.COURSE_4,
    shortDescription: 'AI-PROMPT-001 — Prompt engineering masterclass covering multi-modal design, prompt chaining, and LLM APIs for developers and AI specialists.',
    price: {
      aud: 2999,
      currency: 'AUD',
      earlyBird: 2749
    },
    stripeConfig: {
    priceId: 'price_1T9bE4Ku1nyc322UbNq5lkRB',
    productId: 'prod_U7qvwgJrNExnmx'
  },
    
    snapshot: {
      whoItsFor: [
        'Developers building AI-powered applications',
        'AI Specialists and Machine Learning Engineers',
        'Data Scientists working with LLMs',
        'Content Strategists designing AI workflows',
        'IT Professionals architecting AI solutions'
      ],
      accreditation: 'Industry-leading prompt engineering certification with capstone project review'
    },
    
    logistics: {
      classSize: 'Maximum 20 participants for personalised capstone feedback',
      requirements: [
        'Strong programming experience (Python, JavaScript, or similar)',
        'Understanding of APIs and software architecture',
        'Familiarity with AI/ML concepts and terminology',
        'Development environment with API access capabilities',
        'Commitment to completing hands-on projects and capstone'
      ]
    },
    
    overview: 'This definitive 120-hour masterclass takes technical and creative professionals from foundational prompt engineering principles to advanced, production-grade AI application development. You\'ll master multi-modal prompt design, API-level instructions, sophisticated prompt chaining architectures, function calling patterns, and the complete lifecycle of building, testing, and deploying prompt-based systems. Through intensive hands-on projects, you\'ll work with leading LLM platforms including OpenAI GPT-4, Claude, Gemini, and Azure OpenAI Service. The course culminates in an expert-reviewed capstone project where you\'ll architect and build a complete AI-powered application. Perfect for developers, AI specialists, data scientists, and IT professionals architecting next-generation intelligent systems for Australian enterprises.',
    
    outcomes: [
      'Design and optimise advanced prompts using zero-shot, few-shot, chain-of-thought, and tree-of-thought techniques',
      'Architect multi-modal prompt systems integrating text, images, audio, and structured data',
      'Implement API-level prompt engineering with function calling, tools, and structured outputs',
      'Build sophisticated prompt chains and agent architectures for complex workflows',
      'Develop production-grade prompt templates with dynamic variables, conditionals, and validation',
      'Evaluate and benchmark prompt performance using quantitative metrics and A/B testing',
      'Implement security controls for prompt injection, data leakage, and adversarial inputs',
      'Deploy and monitor prompt-based applications with observability, versioning, and cost optimisation'
    ],
    
    audience: [
      'Software developers building AI-powered applications',
      'AI specialists and machine learning engineers',
      'Data scientists working with large language models',
      'Content strategists designing conversational AI workflows',
      'IT professionals architecting enterprise AI solutions',
      'Product managers overseeing AI feature development',
      'Technical architects evaluating LLM integration strategies'
    ],
    
    prerequisites: [
      'Strong programming skills in Python, JavaScript, or similar languages',
      'Experience with REST APIs and software development',
      'Understanding of fundamental AI/ML concepts',
      'Familiarity with JSON, data structures, and software architecture',
      'Access to LLM API services (OpenAI, Anthropic, or similar)'
    ],
    
    modules: [
      {
        title: 'Foundations of Prompt Engineering',
        duration: '15 hours',
        topics: [
          'Understanding large language model architecture and behaviour',
          'Core prompting principles: clarity, context, constraints',
          'Zero-shot, one-shot, and few-shot learning patterns',
          'Prompt structure, delimiters, and formatting best practices',
          'Temperature, top-p, and other generation parameters',
          'Common failure modes and debugging techniques'
        ]
      },
      {
        title: 'Advanced Prompting Techniques',
        duration: '18 hours',
        topics: [
          'Chain-of-thought (CoT) reasoning and step-by-step decomposition',
          'Tree-of-thought and self-consistency methods',
          'ReAct (Reasoning + Acting) patterns',
          'Self-critique, reflection, and iterative refinement',
          'Prompt compression and token optimisation',
          'Contextual embeddings and retrieval-augmented generation (RAG)',
          'Multi-turn conversation design and memory management'
        ]
      },
      {
        title: 'Multi-Modal Prompt Design',
        duration: '16 hours',
        topics: [
          'Vision-language models and image-based prompting',
          'Audio transcription and audio-to-text prompt patterns',
          'Text-to-image generation with DALL-E, Midjourney, Stable Diffusion',
          'Cross-modal reasoning and unified prompt architectures',
          'Video understanding and temporal reasoning',
          'Structured data integration (CSV, JSON, databases)',
          'Document analysis and multimodal RAG systems'
        ]
      },
      {
        title: 'API-Level Prompt Engineering',
        duration: '18 hours',
        topics: [
          'OpenAI API, Anthropic Claude API, Google Gemini API deep dives',
          'Function calling and tool use patterns',
          'Structured outputs with JSON mode and schema enforcement',
          'System messages, role-based prompting, and instruction hierarchies',
          'Streaming responses and async processing',
          'Batch processing and cost optimisation strategies',
          'Fine-tuning custom models for specialised prompting'
        ]
      },
      {
        title: 'Prompt Chaining and Agent Architectures',
        duration: '20 hours',
        topics: [
          'Sequential prompt chains for complex workflows',
          'Conditional branching and dynamic routing',
          'Agent frameworks: LangChain, AutoGPT, BabyAGI',
          'Tool-augmented agents with function calling',
          'Multi-agent collaboration and orchestration',
          'State management and conversation persistence',
          'Error handling, retries, and fallback strategies',
          'Autonomous task decomposition and planning'
        ]
      },
      {
        title: 'Testing, Evaluation, and Security',
        duration: '13 hours',
        topics: [
          'Quantitative evaluation metrics for prompt quality',
          'A/B testing and statistical significance',
          'Human evaluation frameworks and feedback loops',
          'Adversarial testing and red-teaming prompts',
          'Prompt injection defences and input sanitisation',
          'Data leakage prevention and privacy controls',
          'Bias detection and fairness evaluation',
          'Compliance with Australian Privacy Principles in prompt design'
        ]
      },
      {
        title: 'Production Deployment and Monitoring',
        duration: '10 hours',
        topics: [
          'Prompt versioning and template management',
          'Observability: logging, tracing, and debugging',
          'Performance monitoring and latency optimisation',
          'Cost tracking and budget controls',
          'Scaling strategies for high-volume applications',
          'CI/CD pipelines for prompt-based systems',
          'Incident response and prompt rollback procedures',
          'Australian regulatory compliance in production AI systems'
        ]
      },
      {
        title: 'Capstone Project',
        duration: '10 hours (+ independent work)',
        topics: [
          'Capstone project scope and requirements definition',
          'Building a complete prompt-based application end-to-end',
          'Applying advanced techniques in real-world scenarios',
          'Expert review and personalised feedback sessions',
          'Presentation and documentation of final project',
          'Portfolio development for AI engineering roles'
        ]
      }
    ],
    
    format: {
      type: 'Self-paced with expert capstone review and live masterclasses',
      includes: [
        '120 hours of comprehensive video instruction',
        'Hands-on coding projects and prompt design exercises',
        'API access credits for OpenAI, Anthropic, and Google AI',
        'Expert 1-on-1 review of your capstone project',
        'Monthly live masterclasses with industry practitioners',
        'Access to private prompt engineering community',
        'Case studies from Australian enterprise AI implementations',
        'Prompt template library and reusable code frameworks',
        'Certificate of completion with capstone showcase',
        '18 months access to all course materials and updates',
        'Lifetime access to alumni network and job board'
      ]
    },
    
    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Start self-paced content anytime; capstone reviews scheduled March, June, September, December'
    },
    
    // Pathway relationships
    pathways: [
      'ai-foundations-professional',
      'ai-security-pathway'
    ],
    
    skillsTaught: [
      'Advanced prompt engineering',
      'Multi-modal AI systems',
      'LLM application architecture',
      'RAG and retrieval systems',
      'Prompt chaining and agent orchestration',
      'Prompt testing and evaluation'
    ],
    
    hasCapstone: true,
    
    nextCourses: [
      'ai-security-pathway'
    ],
    
    faqs: [
      {
        question: 'What programming languages are used in this course?',
        answer: 'The course primarily uses Python for API integration and prompt orchestration, with JavaScript examples for web applications. However, prompt engineering principles are language-agnostic—you can apply the techniques in any language that calls LLM APIs. All code is provided with detailed explanations.'
      },
      {
        question: 'Do I need prior AI or machine learning experience?',
        answer: 'You should understand fundamental AI/ML concepts (what models are, training vs inference, etc.), but you don\'t need deep machine learning expertise. This course focuses on using pre-trained LLMs effectively through prompting, not building models from scratch. Strong programming skills are essential.'
      },
      {
        question: 'What AI platforms and APIs will we work with?',
        answer: 'You\'ll gain hands-on experience with OpenAI GPT-4, Anthropic Claude, Google Gemini, Azure OpenAI Service, and open-source models. We provide API credits to get started. The course teaches platform-agnostic principles, so you can apply techniques to any LLM.'
      },
      {
        question: 'What is the capstone project, and how does expert review work?',
        answer: 'The capstone is a complete prompt-based application you design and build—examples include conversational agents, content generation systems, data analysis tools, or workflow automation. You\'ll receive detailed written feedback and a 30-minute live review session with an expert prompt engineer to refine your work.'
      },
      {
        question: 'How much time should I allocate each week?',
        answer: 'We recommend 10-12 hours per week over 10-12 weeks for the self-paced content, plus dedicated time for your capstone project. This is intensive, hands-on work—expect to write significant code and iterate on prompts extensively. Many participants spread the course over 3-4 months while working full-time.'
      },
      {
        question: 'Does this course cover prompt engineering for specific industries?',
        answer: 'Yes, we include case studies and examples from healthcare, finance, legal, government, education, and other sectors relevant to Australian enterprises. You\'ll learn to adapt prompt engineering techniques to industry-specific requirements, compliance constraints, and use cases.'
      },
      {
        question: 'Will this course help me get a job as an AI engineer or prompt engineer?',
        answer: 'This course provides the technical depth and portfolio project (your capstone) needed for AI engineering and prompt engineering roles. Many graduates transition into AI developer positions, ML engineering roles, or specialised prompt engineering positions at organisations deploying LLM-based systems.'
      },
      {
        question: 'How does this course address Australian privacy and compliance?',
        answer: 'Throughout the course, we cover prompt design considerations for Australian Privacy Principles (APPs), data residency requirements, and privacy-preserving techniques. The security module specifically addresses compliance obligations when processing Australian data through LLM APIs, including cross-border data transfer implications.'
      }
    ]
};
